import t, { locale } from ".";
const defaultTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const MINUTE_MS = 60 * 1000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;
export const WEEK_MS = DAY_MS * 7;
export const MONTH_MS = DAY_MS * 30;
export const YEAR_MS = DAY_MS * 365;

const formatters: {
  [k: string]: (date: Date, dateRef?: Date | number) => string;
} = {};
export const dateFormats = [
  "date",
  "month-date",
  "datetime",
  "time",
  "hours-minutes",
  "relative",
  "slash",
] as const;
export type DateFormat = (typeof dateFormats)[number];

const toDate = (date: number | string | Date) =>
  typeof date === "number" || typeof date === "string" ? new Date(date) : date;

export const formatTimePeriod = (timeInMS: number): string => {
  const days = Math.floor(timeInMS / DAY_MS);
  const remainingHours = timeInMS % DAY_MS;
  const hours = Math.floor(remainingHours / HOUR_MS);
  const remainingMinutes = remainingHours % HOUR_MS;
  const minutes = Math.floor(remainingMinutes / MINUTE_MS);

  return t("time_period.days", { days, hours, minutes });
};

const formatDate = (
  date: number | string | Date,
  format: DateFormat,
  timeZoneOrNull: string | null, // passing null => user default time zone
  dateRef?: Date | number, // test relative
) => {
  const d = toDate(date);
  const timeZone = timeZoneOrNull ? timeZoneOrNull : defaultTimeZone;
  const formatterKey = format + timeZone;

  switch (format) {
    case "relative":
      if (!formatters[formatterKey])
        formatters[formatterKey] = (date: Date, dateRef?: Date | number) => {
          const elapsedMs = date.getTime() - Number(dateRef ?? Date.now());
          const absElapsedMs = Math.abs(elapsedMs);
          const [elapsed, unit] =
            absElapsedMs / 2 < MINUTE_MS
              ? ([Math.round(elapsedMs / 1000), "seconds"] as const)
              : absElapsedMs / 2 < HOUR_MS
                ? ([Math.round(elapsedMs / MINUTE_MS), "minutes"] as const)
                : absElapsedMs / 2 < DAY_MS
                  ? ([Math.round(elapsedMs / HOUR_MS), "hours"] as const)
                  : absElapsedMs / 2 < WEEK_MS
                    ? ([Math.round(elapsedMs / DAY_MS), "days"] as const)
                    : absElapsedMs / 2 < MONTH_MS
                      ? ([Math.round(elapsedMs / WEEK_MS), "weeks"] as const)
                      : absElapsedMs / 2 < YEAR_MS
                        ? ([Math.round(elapsedMs / MONTH_MS), "months"] as const)
                        : ([Math.round(elapsedMs / YEAR_MS), "years"] as const);
          return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(elapsed, unit);
        };
      return formatters[formatterKey](d, dateRef);
    case "date":
      if (!formatters[formatterKey])
        formatters[formatterKey] = new Intl.DateTimeFormat(locale, {
          year: "numeric",
          month: "short",
          day: "numeric",
          timeZone,
        }).format;
      return formatters[formatterKey](d);
    case "month-date":
      if (!formatters[formatterKey])
        formatters[formatterKey] = new Intl.DateTimeFormat(locale, {
          year: "numeric",
          month: "short",
          timeZone,
        }).format;
      return formatters[formatterKey](d);
    case "datetime":
      if (!formatters[formatterKey])
        formatters[formatterKey] = new Intl.DateTimeFormat(locale, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZone,
        }).format;
      return formatters[formatterKey](d);
    case "time":
      if (!formatters[formatterKey])
        formatters[formatterKey] = new Intl.DateTimeFormat(locale, {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZone,
        }).format;
      return formatters[formatterKey](d);
    case "hours-minutes":
      if (!formatters[formatterKey])
        formatters[formatterKey] = new Intl.DateTimeFormat(locale, {
          hour: "2-digit",
          minute: "2-digit",
          timeZone,
        }).format;
      return formatters[formatterKey](d);
    case "slash":
      if (!formatters[formatterKey])
        formatters[formatterKey] = new Intl.DateTimeFormat(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          timeZone,
        }).format;
      return formatters[formatterKey](d);

    default: {
      const n: never = format;
      throw n;
    }
  }
};
export default formatDate;
